import { v4 as uuidv4 } from 'uuid';
import Constants from "./constants";
import Config from "../config";

export default class Utils {
    static genId() {
        const uuid = uuidv4();
        return uuid;
    }

    static getCurrentLocale = (lng) => {
        let currentLng = lng;
        if (!currentLng) {
            try { currentLng = JSON.parse(localStorage.getItem('locale'))?.lng; } catch { /* ignore */ }
        }
        return currentLng || 'tk';
    }
    
    // Resolves which of entity.name/name2/name3 matches the given (or current) UI language,
    // per the admin-configurable Constants.NAME_LANGUAGE_MAP, falling back to name/name2/name3 in order.
    static getLocalizedName = (entity, lng) => {
        if (!entity) return '';

        const map = Constants.NAME_LANGUAGE_MAP;
        const field = Object.keys(map).find(key => map[key] === Utils.getCurrentLocale(lng)) || 'name';

        return entity[field] || entity.name || entity.name2 || entity.name3 || '';
    };

    // '' for the base language field, '2'/'3' for the alt-language variant, per Constants.NAME_LANGUAGE_MAP
    static getLocalizedSuffix = (lng) => {
        const map = Constants.NAME_LANGUAGE_MAP;
        const field = Object.keys(map).find(key => map[key] === Utils.getCurrentLocale(lng)) || 'name';
        return field === 'name' ? '' : field.replace('name', '');
    };

    // workHistory.salary overrides position.salary when set (> 0); otherwise falls back to the position's base salary
    static getEffectiveSalary = (workHistory) => {
        const salary = parseFloat(workHistory?.salary) || 0;
        return salary > 0 ? salary : (parseFloat(workHistory?.position?.salary) || 0);
    };

    // Resolves entity[`${field}2`]/[`${field}3`] per the current UI language, falling back to entity[field]
    static getLocalizedField = (entity, field, lng) => {
        if (!entity) return '';
        const suffix = Utils.getLocalizedSuffix(lng);
        return entity[`${field}${suffix}`] || entity[field] || '';
    };

    // Same resolution as getLocalizedName, applied to employee's firstName/lastName/middleName(2/3) triplets
    static getLocalizedFullName = (employee, lng) => {
        if (!employee) return '';

        const firstName = Utils.getLocalizedField(employee, 'firstName', lng);
        const lastName = Utils.getLocalizedField(employee, 'lastName', lng);
        const middleName = Utils.getLocalizedField(employee, 'middleName', lng);

        return `${lastName} ${firstName} ${middleName}`.replace(/\s+/g, ' ').trim();
    };

    static langCode = (field) => Constants.LANGUAGES.find(l => l.lng === Constants.NAME_LANGUAGE_MAP[field])?.code || '';

    static getAvatar(user) {
        try {
            return user?.firstName.charAt(0) + user?.lastName.charAt(0);
        } catch (err) { return ''}
    }

    static getAvatarUrl = (user) => {
        if (!user?.avatar) return undefined;
        return `${Config.URL_SERVER}${Config.PATH_PREFIX}${Config.PATH_USER_AVATARS}${user.avatar}`;
    };

    static getEmployeePhotoUrl = (employee) => {
        if (!employee?.photo) return undefined;
        return `${Config.URL_SERVER}${Config.PATH_PREFIX}${Config.PATH_EMPLOYEE_PHOTOS}${employee.photo}`;
    };

    static stringToColor = (name) => {
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        const color = `hsl(${hash % 360}, 60%, 50%)`;
        return color;
    };

    static getFirstChar(s) { return s.charAt(0); }

    // navigator.clipboard is unavailable in non-secure (http) contexts, so fall back
    // to the legacy execCommand approach in that case.
    static copyToClipboard(text) {
        const legacyCopy = () => {
            const el = document.createElement('textarea');
            el.value = text;
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
        };

        try {
            // writeText can also reject asynchronously (denied permission, unfocused
            // document), which a bare try/catch would miss — leaving nothing copied and
            // an unhandled rejection — so the fallback covers the rejection too.
            navigator.clipboard.writeText(text).catch(legacyCopy);
        } catch {
            legacyCopy();
        }
    }

    static xor = (a, b) => !!(a ^ b);

    static getNestedAttribute(obj, path) {
        if (path.includes(".")) return path.split('.').reduce((acc, key) => acc && acc[key], obj);
        else return obj[path];
    }

    static getEmployeeShortName(fullName) {
        if (!fullName) return '';
        const nameParts = fullName.trim().split(" ");

        if (nameParts.length === 1) {
            return nameParts[0]; // Return full name if only one part
        }
    
        const lastName = nameParts[0];
        const firstName = nameParts[1];
        const middleName = nameParts.length > 2 && nameParts[2];
    
        const firstName1stChar = firstName.charAt(0);
        const middleName1stChar = middleName ? middleName.charAt(0) + '.' : '';

        return `${lastName}. ${firstName1stChar}. ${middleName1stChar} `.trim();
    }

    static getLocalizedEmployeeShortName = (employee, lng) => {
        return Utils.getEmployeeShortName(Utils.getLocalizedFullName(employee, lng));
    };

    static genIdByDateTime() {
        let date = new Date();
        let dd = String(date.getDate()).padStart(2, '0');
        let mm = String(date.getMonth() + 1).padStart(2, '0');
        let yyyy = date.getFullYear();
        let hour = String(date.getHours()).padStart(2, '0');
        let minute = String(date.getMinutes()).padStart(2, '0');
    
        return `${yyyy}${mm}${dd}${hour}${minute}`;
    }

    // Date-only DB fields (Sequelize DATEONLY, e.g. dob) round-trip as plain "yyyy-mm-dd"
    // strings with no time component. JS still parses them as UTC midnight, so reading them
    // back with local getters rolls back a day (a year, at a Jan 1 dob) for negative-offset
    // timezones. Read the parts straight off the string instead of going through Date/local getters.
    static _dateOnlyParts(timestamp) {
        if (typeof timestamp === 'string') {
            const match = timestamp.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            if (match) return { yyyy: match[1], mm: match[2], dd: match[3] };
        }
        let date = new Date(timestamp);
        return {
            yyyy: String(date.getFullYear()),
            mm: String(date.getMonth() + 1).padStart(2, '0'),
            dd: String(date.getDate()).padStart(2, '0'),
        };
    }

    static getTimestampDate(timestamp) {
        if (!timestamp) return;
        const { dd, mm, yyyy } = Utils._dateOnlyParts(timestamp);
        return `${dd}.${mm}.${yyyy}`;
    }

    // "dd-Mon-yyyy" (or "dd-Mon-yy" with shortYear), e.g. 17-Nov-2025 / 17-Nov-25.
    // monthsShort is the translated Map from the MonthsShort() hook - Utils has no i18n access,
    // so the localized month name has to come from the caller.
    static getTimestampDateDMY(timestamp, monthsShort, shortYear = false) {
        if (!timestamp) return;
        const { dd, mm, yyyy } = Utils._dateOnlyParts(timestamp);
        const year = shortYear ? yyyy.slice(2, 4) : yyyy;
        return `${dd}-${monthsShort.get(mm)}-${year}`;
    }

    static getTimestampYear(timestamp) {
        if (!timestamp) return;
        return Number(Utils._dateOnlyParts(timestamp).yyyy);
    }

    static getTimestampMonth(timestamp) {
        if (!timestamp) return;
        return Utils._dateOnlyParts(timestamp).mm;
    }

    static getTimestampDay(timestamp) {
        if (!timestamp) return;
        return Utils._dateOnlyParts(timestamp).dd;
    }

    static getTimestampHourMinute(timestamp) {
        let date = new Date(timestamp);
        let hour = String(date.getHours()).padStart(2, '0');
        let minute = String(date.getMinutes()).padStart(2, '0');
        return `${hour}:${minute}`;
    }

    static getTimestampDateHm(timestamp) {
        let date = new Date(timestamp);
        let dd = String(date.getDate()).padStart(2, '0');
        let mm = String(date.getMonth() + 1).padStart(2, '0');
        let yyyy = date.getFullYear();
        let hour = String(date.getHours()).padStart(2, '0');
        let minute = String(date.getMinutes()).padStart(2, '0');
    
        return `${dd}.${mm}.${yyyy} ${hour}:${minute}`;
    }

    static getTimestampTime(time) {
        if (!time) return;
        let hour = String(time?.hours).padStart(2, '0');
        let minute = String(time?.minutes).padStart(2, '0');
        return `${hour}:${minute}`;
    }

    static getYear() {
        let date = new Date();
        let yyyy = date.getFullYear();

        return yyyy;
    }

    static getMonth() {
        let date = new Date();
        let mm = String(date.getMonth() + 1).padStart(2, '0');
    
        return mm;
    }

    static getDay() {
        let date = new Date();
        let dd = String(date.getDate()).padStart(2, '0');
    
        return dd;
    }

    static getMonthYear() {
        let date = new Date();
        let mm = String(date.getMonth() + 1).padStart(2, '0');
        let yyyy = date.getFullYear();

        return `${mm}.${yyyy}`;
    }

    static getCurrentDate() {
        let date = new Date();
        let dd = String(date.getDate()).padStart(2, '0');
        let mm = String(date.getMonth() + 1).padStart(2, '0');
        let yyyy = date.getFullYear();

        return `${dd}-${mm}-${yyyy}`;
    }

    static getCurrentTime() {
        let date = new Date();
        let hour = String(date.getHours()).padStart(2, '0');
        let minute = String(date.getMinutes()).padStart(2, '0');
    
        return `${hour}:${minute}`;
    }
    
    static getCurrentDateISO() {
        let date = new Date();
        let dd = String(date.getDate()).padStart(2, '0');
        let mm = String(date.getMonth() + 1).padStart(2, '0');
        let yyyy = date.getFullYear();

        return `${yyyy}-${mm}-${dd}`;
    }

    static getTimestampDateISO(timestamp) {
        if (!timestamp) return;
        const { dd, mm, yyyy } = Utils._dateOnlyParts(timestamp);
        return `${yyyy}-${mm}-${dd}`;
    }

    static addDays(timestamp, days) {
        if (!timestamp) return;
        const { dd, mm, yyyy } = Utils._dateOnlyParts(timestamp);
        const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
        date.setDate(date.getDate() + days);
        return Utils.getTimestampDateISO(date);
    }

    static getFormatTime = (value) => value ? new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--';
    
    static isTimestampSaturday = (date) => {
        const today = new Date(date || Date.now());
        return today.getDay() === 6;
    }

    static getDifferentDays(startDate, endDate) {
        if (!(startDate && endDate)) return;

        const start = new Date(startDate);
        const end = new Date(endDate);
        if (isNaN(start) || isNaN(end)) return;

        // Calculate the number of days (inclusive)
        const diffTime = end.getTime() - start.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;

        return diffDays;
    }
    
    // get all days in the current month
    static getAllDaysInMonth = (year, month) => {
        const daysInMonth = new Date(year, month, 0).getDate();
        const daysArray = [];
    
        for (let day = 1; day <= daysInMonth; day++) {
            const dayString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            daysArray.push(dayString);
        }
    
        return daysArray;
    }

    static getSundayCount = (period, uptoDay) => {
        const [year, month] = period.split('-').map(Number);
        const daysInMonth = uptoDay || new Date(year, month, 0).getDate(); // last day of month
        let count = 0;
        for (let day = 1; day <= daysInMonth; day++) {
            if (new Date(year, month - 1, day).getDay() === 0) count++;
        }
        return count;
    };
    
    static getDaysInMonth(year, month) {
        return new Date(year, month, 0).getDate();
    }
    
    static errorHandling (data) {
        let content = '';
        data.map((item, index) => { if (!item.key) content += `${index + 1}) ${item.value}. ` });

        return content;
    }

    static allowedModule(modules, module) {
        if (!modules) return false;
        if (modules.find(m => m.module === module && m.allow)) return true;
        return false;
    }

    static allowedModel(permissions, entityType, actionType) {
        if (!permissions) return false;
        if (permissions.find(p => p.model === entityType && p[actionType])) return true;
        return false;
    }

    static truncateText = (text, size) => {
        return text?.length > size ? text.slice(0, size) + '...' : text;
    };

    static capitalizeFirstLetter = (text) => {
        return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    }

    static toggleSidebar() {
        let el = 'body'.trim();
        document.querySelector(el).classList.toggle('toggle-navbar');
    }

    static numberToTurkmenText(input) {
        let num = typeof input === "string" ? parseFloat(input) : input;
        if (isNaN(num) || num < 0 || num >= 1e15) return "San 0 bilen 1 trillion arasynda bolmaly.";

        const ones = ["nol", "bir", "iki", "üç", "dört", "bäş", "alty", "ýedi", "sekiz", "dokuz"];
        const teens = ["on", "on bir", "on iki", "on üç", "on dört", "on bäş", "on alty", "on ýedi", "on sekiz", "on dokuz"];
        const tens = ["", "", "ýigrimi", "otuz", "kyrk", "elli", "altmyş", "ýetmiş", "segsen", "togsan"];
        const scales = ["", "müň", "million", "milliard", "trillion"];

        if (num < 0) return "minus"; // Handle negative numbers
        if (num === 0) return "0"; // Handle zero
        const [dollars, cents] = num.toFixed(2).split('.'); // Split dollars and cents

        // Convert dollars to words
        function convertChunk(chunk) {
            if (chunk === "000") return "";

            let result = [];
            const n = parseInt(chunk);

            // Hundreds place
            if (Math.floor(n / 100) > 0) {
                result.push(ones[Math.floor(n / 100)] + " ýüz");
                chunk = (n % 100).toString().padStart(2, '0');
            }

            // Tens and ones place
            const chunkNum = parseInt(chunk);
            if (chunkNum >= 20) {
                result.push(tens[Math.floor(chunkNum / 10)]);
                if (chunkNum % 10 > 0) result.push(ones[chunkNum % 10]);
            }
            else if (chunkNum >= 10) result.push(teens[chunkNum - 10]);
            else if (chunkNum > 0) result.push(ones[chunkNum]);
            return result.join(" ");
        }

        let dollarsNum = parseInt(dollars); // Break number into chunks
        let words = [];

        for (let i = 0; dollarsNum > 0; i++) {
            if (dollarsNum % 1000 !== 0) {
                const chunk = (dollarsNum % 1000).toString().padStart(3, '0');
                const chunkWords = convertChunk(chunk);
                if (chunkWords) words.unshift(chunkWords + (i > 0 ? " " + scales[i] : ""));
            }
            dollarsNum = Math.floor(dollarsNum / 1000);
        }

        // Combine dollar and cent parts
        const dollarWords = words.length ? words.join(" ") + " manat" : "";
        const centWords = cents + " teňňe";
        return (dollarWords + " " + centWords).trim();
    }

    static formatCurrency(amount = 0, currencyCode, locale = 'en-US', minFractionDigits = 2, maxFractionDigits = 2) {
        if (!currencyCode) return;

        const formatter = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: minFractionDigits,
            maximumFractionDigits: maxFractionDigits,
        });

        return formatter.format(amount);
    }

    static formatNumber(value, locale = 'en-US', options = { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: true }) {
        if (value === undefined || value === null) return;
        
        return value.toLocaleString(locale, options);
    }
  
    static float2 = (num) => Number.parseFloat(num.toFixed(2));;

    static formatNumberForPaymentOrder(value) {
        let formatted = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2 }).format(value).replace(',', '-');
        return formatted;
        // return cents === "00" ? (dollars+'=') : formatted;
    }

    static getBase64FromUrl = async (url) => {
        try {
            if (!url) {
                console.error('Invalid or empty URL');
                return null;
            }

            const response = await fetch(url);
            if (!response.ok) {
                console.error(`Failed to fetch image. Status: ${response.status} ${response.statusText}`);
                return null;
            }

            const blob = await response.blob();

            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error('Error converting URL to base64:', error.message);
            return null;
        }
    };

    static getReversedMonths = (monthsMap, currentMonth, currentYear) => {
        const monthsArray = Array.from(monthsMap);
        const currentIndex = monthsArray.findIndex(([key]) => key === currentMonth);

        // group1: from currentMonth to end of year (currentYear)
        const group1 = monthsArray
            .slice(currentIndex)
            .map(([key]) => ({ month: key, year: currentYear - 1 }));

        // group2: from start to month before currentMonth (previous year)
        const group2 = monthsArray
            .slice(0, currentIndex)
            .map(([key]) => ({ month: key, year: currentYear }));

        // rotated: start from currentMonth, then wrap around
        const rotated = [...group1, ...group2];

        return {
            reversedMonths: rotated.reverse(),
            reversedMonthsGroup1: group1.reverse(),
            reversedMonthsGroup2: group2.reverse(),
        };
    };

    static hasDuplicateItem = (items, findBy, value) => {
        return items.some(item => String(item[findBy]) === String(value));
    };

    static getTimeDiff = (diff, t, simple) => {
        if (!diff) return '';
        if (diff.status === 'ON_TIME') return null;

        const parts = [];
        if (diff.hours) parts.push(t('HOURS', { count: diff.hours }));
        if (diff.minutes) parts.push(t('MINUTES', { count: diff.minutes }));

        const time = parts.join(' ');

        if (!simple) {
            switch (diff.status) {
                case 'LATE': return t('LATE_BY', { time });
                case 'EARLY': return t('EARLY_BY', { time });
                case 'OVERTIME': return t('OVERTIME_BY', { time });
                case 'EARLY_LEAVE': return t('LEFT_EARLY_BY', { time });
                default: return '';
            }
        }
        else {
            switch (diff.status) {
                case 'LATE': return `-${time}`;
                case 'EARLY': return `+${time}`;
                case 'OVERTIME': return `+${time}`;
                case 'EARLY_LEAVE': return `-${time}`;
                default: return '';
            }
        }
    };

    static getFileExtension = (fileName) => {
        if (!fileName) return;
        return fileName.split('.').pop().split(/\#|\?/)[0].toLowerCase();
    }

    static stripHtml = (htmlString) => {
        const doc = new DOMParser().parseFromString(htmlString, 'text/html');
        return doc.body.textContent || "";
    };

    static generateSKU = ({ name, prefix = '' }) => {
        const short = name
            .replace(/[^a-zA-Z0-9 ]/g, '')
            .split(' ')
            .map(w => w[0])
            .join('')
            .toUpperCase()
            .slice(0, 3);

        const timestamp = Date.now().toString().slice(-4);
        const random = Math.floor(100 + Math.random() * 900); // 3 digits

        return `${prefix}${short}-${timestamp}-${random}`;
    }

    static timeAgo = (timestamp, t) => {
        const diff = Math.floor((new Date() - new Date(timestamp)) / 1000); // seconds

        if (diff < 60) return t('TIME_AGO_SECONDS', { count: diff });
        if (diff < 3600) return t('TIME_AGO_MINUTES', { count: Math.floor(diff / 60) });
        if (diff < 86400) return t('TIME_AGO_HOURS', { count: Math.floor(diff / 3600) });
        if (diff < 86400 * 2) return t('TIME_AGO_YESTERDAY');
        return t('TIME_AGO_DAYS', { count: Math.floor(diff / 86400) });
    };

    static isBirthdayToday = (dob) => {
        const today = new Date();
        const birth = new Date(dob);
        return today.getMonth() === birth.getMonth() && today.getDate() === birth.getDate();
    };
    
    static formatWorkExperienceYear = (years) => {
        const floor = Math.floor(years);
        return floor < years ? `${floor}+` : `${floor}`;
    }

    // Splits text around URLs (with or without a protocol, e.g. "www.x.com") so callers
    // can render the link segments as clickable anchors while keeping the rest as plain text.
    static URL_REGEX = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;

    static linkifyText = (text) => {
        if (!text) return [];

        return text.split(Utils.URL_REGEX).filter(part => part !== undefined && part !== '').map((part) => {
            const isLink = /^(https?:\/\/|www\.)/i.test(part);
            return { type: isLink ? 'link' : 'text', value: part };
        });
    };
}